@if (showPinnedCopy()) {
  <!-- A copy of the header row that stays at the top of the page while a table wider than the screen scrolls sideways under it. -->
  <div class="pinned-head" aria-hidden="true">
    <div class="pinned-head__clip">
      <table class="if-table pinned-head__table" #pinnedTable>
        <thead>
          <tr>
            <ng-container *ngTemplateOutlet="headCells; context: { pinned: true }"></ng-container>
          </tr>
        </thead>
      </table>
    </div>
  </div>
}
<div class="table-scroll" #scroll (focusin)="revealFocused($event)">
  <div class="head-sentinel" #headSentinel aria-hidden="true"></div>
  <table class="if-table" #table [attr.aria-busy]="loading() ? 'true' : null">
    @if (caption()) {
      <caption class="visually-hidden">{{ caption() }}</caption>
    }
    <thead #head>
      <tr>
        <ng-container *ngTemplateOutlet="headCells; context: { pinned: false }"></ng-container>
      </tr>
    </thead>
    <tbody>
      <!-- Groupable mode with expandable rows -->
      @if (groupable() && groups().length > 0) {
        @for (group of groups(); track group.id) {
          <tr class="group-header-row" [class.expanded]="isGroupExpanded(group.id)" (click)="toggleGroup(group.id)">
            <td [attr.colspan]="columnCount()" class="group-header-cell">
              <ng-container *ngTemplateOutlet="getTemplate('groupHeader'); context: { $implicit: group, expanded: isGroupExpanded(group.id) }"></ng-container>
            </td>
          </tr>
          @if (isGroupExpanded(group.id)) {
            @for (item of group.items; track item['id']) {
              <tr class="group-item-row" [class.row--selected]="isSelected(item)" [attr.aria-selected]="selectionAria(item)">
                <ng-container *ngTemplateOutlet="rowCells; context: { $implicit: item, isGroupItem: true }"></ng-container>
              </tr>
            }
            @if (group.items.length === 0) {
              <tr class="group-empty-row">
                <td [attr.colspan]="columnCount()" class="group-empty-cell">
                  <ng-container *ngTemplateOutlet="getTemplate('groupEmpty'); context: { $implicit: group }"></ng-container>
                </td>
              </tr>
            }
          }
        }
      }
      <!-- Regular data rows (non-groupable mode or standalone items) -->
      @if (!groupable() || (groupable() && data().length > 0)) {
        @for (item of data(); track rowId(item)) {
          <tr
            [class.row--selected]="isSelected(item)"
            [class.row--expanded]="isExpanded(item)"
            [class.row--checked]="selectable() && isChecked(item)"
            [class.row--moved]="isMoved(item)"
            [attr.data-row]="$index"
            [attr.aria-selected]="selectionAria(item)"
            [attr.draggable]="rowDraggable() && !editingCell ? 'true' : null"
            (dragstart)="onRowDragStart($event, item)"
            (dragend)="onRowDragEnd()"
            (drop)="onRowDrop($event, item)">
            <ng-container *ngTemplateOutlet="rowCells; context: { $implicit: item, isGroupItem: false }"></ng-container>
          </tr>
          @if (isExpanded(item) && getTemplate('rowDetail')) {
            <tr class="detail-row" [id]="detailId(rowId(item))">
              <td [attr.colspan]="columnCount()" class="detail-cell">
                <ng-container *ngTemplateOutlet="getTemplate('rowDetail'); context: { $implicit: item }"></ng-container>
              </td>
            </tr>
          }
        }
      }
      @if (showSkeleton()) {
        @for (i of skeletonIndexes(); track i) {
          <tr class="skeleton-row" aria-hidden="true">
            @if (selectable()) {
              <td class="select-cell"></td>
            }
            @for (col of columns(); track col) {
              <td [class.hide-sm]="col.hideBelow === 'sm'" [class.hide-md]="col.hideBelow === 'md'" [class.hide-lg]="col.hideBelow === 'lg'" [class.col-dropped]="isDropped(col)">
                <span class="skeleton-bar" [class.skeleton-bar--short]="col.columnType === 'actions' || col.field === 'actions'"></span>
              </td>
            }
          </tr>
        }
      }
      @if (showStateRow) {
        <tr class="state-row">
          <td [attr.colspan]="columnCount()" class="state-cell">
            <ng-container *ngTemplateOutlet="activeStateTemplate"></ng-container>
          </td>
        </tr>
      }
    </tbody>
  </table>
</div>
@if (loading() && !showSkeleton()) {
  <div class="table-loading" aria-hidden="true">
    <app-spinner type="infinitform" />
  </div>
}
<div class="visually-hidden" role="status" aria-live="polite">{{ liveMessage() }}</div>

<ng-template #rowCells let-item let-isGroupItem="isGroupItem">
  @if (selectable()) {
    <td class="select-cell">
      @if (!isGroupItem) {
        <label class="row-check">
          <input
            type="checkbox"
            class="row-check__input"
            [checked]="isChecked(item)"
            [attr.aria-label]="checkLabel(item)"
            (click)="onRowCheck($event, item)" />
          <span class="row-check__box" aria-hidden="true"></span>
        </label>
      }
    </td>
  }
  @for (col of columns(); track col) {
    @let kind = cellKind(col);
    <td
      [class.cell--end]="isEnd(col)"
      [class.cell--numeric]="kind === 'number'"
      [class.cell--date]="kind === 'date' || kind === 'datetime'"
      [class.cell--actions]="col.columnType === 'actions'"
      [class.preview-col]="col.field === 'preview'"
      [class.width-zero]="col.widthZero || (kind === 'template' && col.field === 'actions')"
      [class.truncate-200]="col.truncate === '200'"
      [class.hide-sm]="col.hideBelow === 'sm'"
      [class.hide-md]="col.hideBelow === 'md'"
      [class.hide-lg]="col.hideBelow === 'lg'"
      [class.col-dropped]="isDropped(col)"
      [class.editable-cell]="col.editable"
      [class.editing]="col.field && isEditing(item, col.field)"
      [style.width]="col.width ?? null"
      (dblclick)="onCellDblClick(col, item)">
      @if (col.editable && col.field && isEditing(item, col.field)) {
        <app-input
          type="text"
          [(ngModel)]="editingValue"
          (keydown)="onKeyDown($event, item)"
          (blur)="saveEditing(item)"
          class="inline-edit-input"
          (click)="$event.stopPropagation()" />
      } @else {
        @switch (kind) {
          @case ('template') {
            <ng-container *ngTemplateOutlet="getTemplate(col.field!); context: { $implicit: item, isGroupItem: isGroupItem }"></ng-container>
          }
          @case ('datetime') {
            {{ $any(item[col.field!]) | humandate }}
          }
          @case ('date') {
            {{ $any(item[col.field!]) | date: 'mediumDate' }}
          }
          @case ('number') {
            <span>{{ item[col.field!] }}</span>
          }
          @case ('text') {
            <span
              [class.editable-text]="col.editable"
              (dblclick)="col.editable ? startEditing(item, col.field!) : null">
              {{ item[col.field!] }}
            </span>
          }
        }
      }
    </td>
  }
</ng-template>

<ng-template #headCells let-pinned="pinned">
  @if (selectable()) {
    <th scope="col" class="select-cell">
      <label class="row-check">
        <input
          type="checkbox"
          class="row-check__input"
          [class.select-all]="!pinned"
          [checked]="pageSelection() === 'all'"
          [indeterminate]="pageSelection() === 'some'"
          [disabled]="data().length === 0"
          [attr.tabindex]="pinned ? -1 : null"
          aria-label="Select all shown"
          (change)="toggleAllShown()" />
        <span class="row-check__box" aria-hidden="true"></span>
      </label>
    </th>
  }
  @for (column of columns(); track column) {
    <th
      scope="col"
      [attr.aria-sort]="ariaSort(column)"
      [class.if-table-cell-sortable]="isSortable(column)"
      [class.cell--end]="isEnd(column)"
      [class.hide-sm]="column.hideBelow === 'sm'"
      [class.hide-md]="column.hideBelow === 'md'"
      [class.hide-lg]="column.hideBelow === 'lg'"
      [class.col-dropped]="isDropped(column)"
      [class.width-zero]="column.widthZero"
      [style.width]="pinned ? null : (column.width ?? null)">
      @if (isSortable(column)) {
        @let direction = sortDirectionOf(column);
        <button type="button" class="sort-button" [class.sort-button--active]="!!direction" [attr.tabindex]="pinned ? -1 : null" (click)="sort(column)">
          <span>{{ column.headerText }}</span>
          <svg class="sort-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
            @if (direction === 'asc') {
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
            } @else if (direction === 'desc') {
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            } @else {
              <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
            }
          </svg>
        </button>
      } @else if (column.columnType === 'actions' || column.field === 'actions') {
        <span class="visually-hidden">{{ column.headerText || 'Actions' }}</span>
      } @else {
        <span class="th-label">{{ column.headerText }}</span>
      }
    </th>
  }
</ng-template>
